import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Handle subscription required error
    if (error.response?.status === 402) {
      window.location.href = '/subscribe';
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const getTermsOfService = () => api.get('/terms-of-service');

// Payment
export const createCheckoutSession = (originUrl) => api.post('/payments/create-checkout', { origin_url: originUrl });
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
export const getSubscriptionStatus = () => api.get('/payments/subscription-status');

// Cases
export const getCases = () => api.get('/cases');
export const getCase = (id) => api.get(`/cases/${id}`);
export const createCase = (data) => api.post('/cases', data);
export const updateCase = (id, data) => api.put(`/cases/${id}`, data);
export const deleteCase = (id) => api.delete(`/cases/${id}`);

// Collaborators
export const getCollaborators = (caseId) => api.get(`/cases/${caseId}/collaborators`);
export const inviteCollaborator = (caseId, email, role) => api.post(`/cases/${caseId}/collaborators`, { email, role });
export const updateCollaboratorRole = (caseId, userId, role) => api.put(`/cases/${caseId}/collaborators/${userId}`, { role });
export const removeCollaborator = (caseId, userId) => api.delete(`/cases/${caseId}/collaborators/${userId}`);

// Documents
export const getDocuments = (caseId) => api.get(`/cases/${caseId}/documents`);
export const getDocument = (caseId, docId) => api.get(`/cases/${caseId}/documents/${docId}`);
export const getDocumentPreviewUrl = (caseId, docId) => `${API_URL}/api/cases/${caseId}/documents/${docId}/preview`;
export const uploadDocuments = (caseId, files) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return api.post(`/cases/${caseId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const updateDocument = (caseId, docId, data) => api.put(`/cases/${caseId}/documents/${docId}`, data);
export const deleteDocument = (caseId, docId) => api.delete(`/cases/${caseId}/documents/${docId}`);

// Bulk Operations
export const bulkUpdateDocuments = (caseId, documentIds, tags, category) => 
  api.put(`/cases/${caseId}/documents/bulk/update`, { document_ids: documentIds, tags, category });
export const bulkDeleteDocuments = (caseId, documentIds) => 
  api.post(`/cases/${caseId}/documents/bulk/delete`, { document_ids: documentIds });

// Exhibit Labeling
export const getExhibitParties = (caseId) => api.get(`/cases/${caseId}/exhibit-parties`);
export const assignExhibitLabel = (caseId, docId, party, numbering, number = null, stampLabel = false, labelPosition = 'top-right') => 
  api.post(`/cases/${caseId}/documents/${docId}/exhibit-label`, { 
    document_id: docId, 
    party, 
    numbering, 
    number,
    stamp_label: stampLabel,
    label_position: labelPosition
  });
export const removeExhibitLabel = (caseId, docId) => api.delete(`/cases/${caseId}/documents/${docId}/exhibit-label`);
export const getLabeledDocumentUrl = (caseId, docId) => `${API_URL}/api/cases/${caseId}/documents/${docId}/labeled`;

// Final Exhibits
export const getFinalExhibits = (caseId) => api.get(`/cases/${caseId}/final-exhibits`);
export const getFinalExhibitListUrl = (caseId) => `${API_URL}/api/cases/${caseId}/final-exhibits/report`;

// Analysis
export const analyzeCase = (caseId, arguments_text) => api.post(`/cases/${caseId}/analyze`, { case_arguments: arguments_text });
export const getExhibits = (caseId) => api.get(`/cases/${caseId}/exhibits`);
export const getAnalysisHistory = (caseId) => api.get(`/cases/${caseId}/analyses`);
export const getAnalysis = (caseId, analysisId) => api.get(`/cases/${caseId}/analyses/${analysisId}`);
export const compareAnalyses = (caseId, analysisId1, analysisId2) => 
  api.post(`/cases/${caseId}/analyses/compare`, { analysis_id_1: analysisId1, analysis_id_2: analysisId2 });

// Argument Templates
export const getArgumentTemplates = () => api.get('/argument-templates');
export const getArgumentTemplate = (templateId) => api.get(`/argument-templates/${templateId}`);

// Search
export const searchDocuments = (query, caseId = null) => api.post('/search', { query, case_id: caseId });

// Report
export const getReportUrl = (caseId) => `${API_URL}/api/cases/${caseId}/report`;
export const getAiIdentificationReportUrl = (caseId) => `${API_URL}/api/cases/${caseId}/ai-identification-report`;

// Stats
export const getStats = () => api.get('/stats');

// Examination.AI
export const getExaminationOutlines = (caseId = null) => {
  const params = caseId ? `?case_id=${caseId}` : '';
  return api.get(`/examination/outlines${params}`);
};
export const getExaminationOutline = (outlineId) => api.get(`/examination/outlines/${outlineId}`);
export const createExaminationOutline = (data) => api.post('/examination/outlines', data);
export const updateExaminationOutline = (outlineId, data) => api.put(`/examination/outlines/${outlineId}`, data);
export const deleteExaminationOutline = (outlineId) => api.delete(`/examination/outlines/${outlineId}`);
export const generateExaminationOutline = (outlineId, sectionType) => 
  api.post(`/examination/outlines/${outlineId}/generate?section_type=${sectionType}`);
export const exportExaminationOutline = (outlineId, format = 'docx') => 
  api.post(`/examination/outlines/${outlineId}/export?format=${format}`);
export const getExaminationCases = () => api.get('/examination/cases');

export default api;
